import React, { useContext, useState } from "react";
import { Link } from "gatsby";
import Image from "./image";
import PropTypes from "prop-types";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleRight,
  faAngleLeft,
  faShoppingCart,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { GlobalStateContext } from "../context/global-context-provider";
import { checkDarkMode, modifyColor } from "../utils/helpers";

const LogoImage = styled(Link)`
  width: 100%;
  padding: 30px 0 20px 40px;
  filter: ${({ theme }) =>
    theme.isDarkMode ? "grayscale(1) brightness(0) invert(1)" : "unset"};
`;
const StyledMenu = styled.div`
  transition: transform 0.3s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  background-color: ${({ theme }) =>
    theme.colors.headerBackground || theme.colors.secondary};
  font-family: ${({ theme }) => theme.fontLinks.fontFamily};
  overflow-x: hidden;
  transform: ${({ $burgerOpen }) =>
    $burgerOpen ? "translateX(0%)" : "translateX(-100%)"};
  width: 450px;
  flex-direction: column;
  justify-content: space-between;
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  height: 100vh;
  padding: 0;
  margin: 0;
  box-shadow: 0 10px 10px rgba(0, 0, 0, 0.16);
  z-index: 9999;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: ${({ $burgerOpen }) => ($burgerOpen ? "100vw" : "0")};
  }
  & a,
  & button {
    font-family: inherit;
  }
`;
const MenuWrapper = styled.ul`
  position: relative;
  padding-inline: 3rem;
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.headerLink || theme.colors.black};
  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }
`;
const BaseButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  text-decoration: none;
  padding: 0;
  cursor: pointer;
  font-size: inherit;
  color: var(--color-black);
  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }
`;
const HasChildren = styled(BaseButton)`
  display: flex;
  align-items: baseline;
  color: ${({ theme }) => theme.colors.headerLink || theme.colors.black};
`;

const NavItem = styled.li`
  list-style-type: none;
  display: flex;
  flex-direction: column;
  padding: 1rem 0;
  font-weight: 500;
  width: 100%;
  a {
    margin-top: 0;
    margin-bottom: 0;
  }
`;
const UlSubLinks = styled.ul`
  display: flex;
  flex-direction: column;
  padding: 60px 0 20px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.primary};
  width: 100%;
  height: fit-content;
`;
const Dropdown = styled.div`
  --transition-duration: 0.4s;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  justify-content: start;
  background-color: ${({ theme }) =>
    theme.colors.headerBackground || theme.colors.secondary};
  transition-property: transform;
  transition-timing-function: ease-in-out;
  transition-duration: ${({ $mainMenuIsOpening }) =>
    $mainMenuIsOpening ? "0s" : "var(--transition-duration)"};
  transition-delay: ${({ $mainMenuIsClosing }) =>
    $mainMenuIsClosing ? "var(--transition-duration)" : "0s"};
  transform: ${({ $isOpen }) =>
    $isOpen ? "translateX(0)" : "translateX(100%)"};
  position: absolute;
  padding-inline: 40px;
  inset: 0;
  align-items: flex-start;
  overflow-y: scroll;
  z-index: 99;
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
`;
const ExtraLink = styled(Link)`
  padding: 25px 0;
  text-decoration: none;
  display: flex;
  align-items: center;
  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }
`;
const CartQuantity = styled.div`
  display: flex;
  align-items: center;
  margin: 0;
  p {
    font-weight: 600;
    margin: 0 0 0 5px;
  }
`;
const BottomContent = styled.div`
  background: ${({ theme }) => checkDarkMode("#f6f6f6", theme)};
  width: 100%;
  padding: 1rem 2rem;
  display: flex;
  flex-flow: column wrap;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding: 1rem 2rem;
  }
  div {
    padding: 15px 10px 0 10px;
    p {
      font-size: 0.9rem;
    }
  }
`;
const ButtonStyle = styled(Link)`
  background-color: ${({ theme }) =>
    theme.colors.headerButton || theme.colors.primary};
  color: ${({ theme }) => theme.colors.headerButtonText || theme.colors.white};
  border: 2px solid
    ${({ theme }) => theme.colors.headerButton || theme.colors.primary};
  font-size: 1rem;
  line-height: initial;
  text-decoration: none;
  transition: 0.3s all ease-in-out;
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 7.5px 45px;
  border-radius: 4px;
  max-width: 200px;
  margin: 0.5rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    max-width: none;
  }
  &:first-child {
    background-color: transparent;
    border: 2px solid ${({ theme }) => checkDarkMode("#000", theme)};
    color: ${({ theme }) => checkDarkMode("#000", theme)};
    box-sizing: border-box;
    @media (hover: hover) {
      &:hover {
        background-color: ${({ theme }) => checkDarkMode("#f6f6f6", theme)};
        border-color: #000;
      }
    }
  }
  @media (hover: hover) {
    &:hover {
      text-decoration: none;
      cursor: pointer;
      background-color: ${({ theme }) =>
        modifyColor(theme.colors.headerButton || theme.colors.primary, -40)};
      border-color: ${({ theme }) =>
        modifyColor(theme.colors.headerButton || theme.colors.primary, -40)};
    }
  }
`;
const BackButton = styled(BaseButton)`
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
  min-height: 3rem;
  padding: 0;
`;
const CloseButton = styled(BaseButton)`
  position: absolute;
  top: 2rem;
  right: 2rem;
  padding: 4px 7px;
  border-radius: var(--border-radius);
  transition: all 0.3s ease-in-out;
  z-index: 99;
  @media (hover: hover) {
    &:hover {
      background: #ddd;
    }
  }
`;

const SlideNav = ({ links, open, onClose, burgerOpen, mainMenuState }) => {
  const [activeChild, setActiveChild] = React.useState();

  React.useEffect(() => {
    setActiveChild(undefined);
  }, [burgerOpen]);

  return (
    <Dropdown
      $isOpen={open}
      $mainMenuIsOpening={mainMenuState === "opening"}
      $mainMenuIsClosing={mainMenuState === "closing"}
    >
      {open && (
        <>
          <BackButton aria-label="back-button" onClick={onClose}>
            <FontAwesomeIcon
              icon={faAngleLeft}
              size="1x"
              style={{ marginRight: "10px" }}
            />
            Tilbake
          </BackButton>
          <UlSubLinks>
            {links.subLinks.map((item) => (
              <NavItem key={item.id}>
                {item.subLinks?.length > 0 ? (
                  <>
                    <HasChildren onClick={() => setActiveChild(item.id)}>
                      {item.title}
                      <FontAwesomeIcon
                        icon={faAngleRight}
                        size="xs"
                        style={{ marginLeft: "10px" }}
                      />
                    </HasChildren>
                    <SlideNav
                      links={item}
                      open={activeChild === item.id}
                      onClose={() => setActiveChild(undefined)}
                      burgerOpen={burgerOpen}
                      mainMenuState={mainMenuState}
                    />
                  </>
                ) : (
                  <NavLink to={item.url} activeStyle={{ fontWeight: "bold" }}>
                    {item.title}
                  </NavLink>
                )}
              </NavItem>
            ))}
          </UlSubLinks>
          <ExtraLink to={links.url} activeStyle={{ fontWeight: "bold" }}>
            {links.description}
            <FontAwesomeIcon
              icon={faAngleRight}
              size="xs"
              style={{ marginLeft: "10px" }}
            />
          </ExtraLink>
        </>
      )}
    </Dropdown>
  );
};

const MenuSideNav = ({ setBurgerOpen, burgerOpen, wp, headerNav }) => {
  const [activeDropdown, setActiveDropdown] = React.useState(null);
  const [mainMenuState, setMainMenuState] = React.useState(
    burgerOpen ? "opened" : "closed"
  );
  const [totalQtyOfProducts, setTotalQtyOfProducts] = useState(0);
  const { cart } = useContext(GlobalStateContext);

  React.useEffect(() => {
    const handleEscape = (e) => e.keyCode === 27 && setBurgerOpen(undefined);
    window.addEventListener("keyup", handleEscape);
    return () => window.removeEventListener("keyup", handleEscape);
  }, []);

  React.useEffect(() => {
    setActiveDropdown(burgerOpen);
    if (!activeDropdown) {
      if (burgerOpen) {
        setMainMenuState("opening");
      }
    } else {
      if (!burgerOpen) {
        setMainMenuState("closing");
      }
    }
  }, [burgerOpen]);

  React.useEffect(() => {
    setTotalQtyOfProducts(
      cart.reduce((totalQtyOfProducts, item) => {
        return totalQtyOfProducts + item.quantity;
      }, 0)
    );
  }, [cart]);

  const [navItems] = React.useState(() =>
    headerNav?.menuItems.nodes
      .filter((node) => node.parentId === null)
      .map((node) => {
        return {
          id: node.id,
          url: node.url,
          title: node.label,
          description: node.description,
          isButton: node.cssClasses.includes("button"),
          subLinks: node.childItems?.nodes?.map((node) => ({
            id: node.id,
            url: node.url,
            title: node.label,
            subLinks: node.childItems?.nodes?.map((node) => ({
              id: node.id,
              url: node.url,
              title: node.label,
            })),
          })),
        };
      })
  );

  const [menuItems] = React.useState(() =>
    navItems?.filter((item) => !item.isButton)
  );
  const [buttons] = React.useState(() =>
    navItems?.filter((item) => item.isButton)
  );

  return (
    <StyledMenu
      $burgerOpen={burgerOpen}
      onTransitionEnd={() => setMainMenuState(burgerOpen ? "opened" : "closed")}
    >
      {burgerOpen && (
        <>
          {wp?.crbThemeOptions?.crbLogoHeader?.media && (
            <LogoImage to="/">
              <Image
                alt="Logotype"
                image={wp?.crbThemeOptions?.crbLogoHeader?.media}
                loading="eager"
              />
            </LogoImage>
          )}
          <CloseButton
            aria-label="close-button"
            onClick={() => setBurgerOpen(undefined)}
          >
            <FontAwesomeIcon size="xl" icon={faTimes} />
          </CloseButton>
          <MenuWrapper>
            {menuItems?.map((node) => (
              <NavItem key={node.id}>
                {node.subLinks?.length > 0 ? (
                  <HasChildren onClick={() => setActiveDropdown(node.id)}>
                    {node.title}
                    <FontAwesomeIcon
                      icon={faAngleRight}
                      size="xs"
                      style={{ marginLeft: "10px" }}
                    />
                  </HasChildren>
                ) : node.url === wp.wcSettings?.cartUrl ? (
                  <NavLink to={node.url}>
                    <CartQuantity>
                      <FontAwesomeIcon size="1x" icon={faShoppingCart} />
                      {cart.length > 0 ? <p>{totalQtyOfProducts}</p> : ""}
                    </CartQuantity>
                  </NavLink>
                ) : (
                  <NavLink to={node.url} activeStyle={{ fontWeight: "bold" }}>
                    {node.title}
                  </NavLink>
                )}
                {node.subLinks?.length > 0 && (
                  <SlideNav
                    links={node}
                    open={activeDropdown === node.id}
                    onClose={() => setActiveDropdown(undefined)}
                    burgerOpen={burgerOpen}
                    mainMenuState={mainMenuState}
                  />
                )}
              </NavItem>
            ))}
          </MenuWrapper>
          <BottomContent>
            {buttons?.map((button) => (
              <ButtonStyle key={button.id} to={button.url}>
                {button.title}
              </ButtonStyle>
            ))}
            {wp.crbThemeOptions?.crbMenuExtraText && (
              <div
                dangerouslySetInnerHTML={{
                  __html: wp.crbThemeOptions?.crbMenuExtraText,
                }}
              ></div>
            )}
          </BottomContent>
        </>
      )}
    </StyledMenu>
  );
};

MenuSideNav.propTypes = {
  setBurgerOpen: PropTypes.func,
  burgerOpen: PropTypes.bool,
  wp: PropTypes.object,
  headerNav: PropTypes.object,
};

SlideNav.propTypes = {
  links: PropTypes.shape({
    url: PropTypes.string,
    description: PropTypes.string,
    subLinks: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
        subLinks: PropTypes.arrayOf(PropTypes.object),
      })
    ),
  }),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  burgerOpen: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  mainMenuState: PropTypes.oneOf(["opening", "closing", "closed", "opened"]),
};

export default MenuSideNav;
